import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { L10nTranslationService } from 'angular-l10n';
import { delay, map, Observable, of } from 'rxjs';
import { id } from '@wonder-core-ng/interfaces/id.type';
import { BI_CORE_CONFIG } from '../config';
import { isPathChoiceRequired, PathChoiceRequiredError } from './path-choice.utils';
import { IQueryEngineFilters } from './interfaces/query-engine.interface';
import {
  IAttributeMetadataItem,
  IAttributeMetadataSummary,
  IUpsertAttributeMetadata,
} from './interfaces/attribute-metadata.interface';
import {
  ITableMetadataItem,
  IUpsertTableMetadata,
} from './interfaces/table-metadata.interface';
import {
  BucketMethod,
  IColumnBuckets,
  IForecastRequest,
  IForecastResponse,
  IQueryConfigData,
  IQueryResponseData,
  JoinPathLevel,
  JoinRelationEntry,
  QueryConfig,
} from './interfaces/query.interface';
import { logisticsMocks } from './mocks/query-engine.mocks';
import { IAiValidation } from './ai-origin';

@Injectable({
  providedIn: 'root',
})
export class QueryEngineApiService {
  private readonly httpClient = inject(HttpClient);
  private readonly translationService = inject(L10nTranslationService);
  private readonly config = inject(BI_CORE_CONFIG);

  private readonly route = `${this.config.apiBaseUrl}/query-engine`;

  loadData<T>(query: IQueryEngineFilters, tenantId: id): Observable<T> {
    const mockData = logisticsMocks[query.id as number];
    if (mockData) {
      return of(mockData as T).pipe(delay(300));
    }
    const params: { [key: string]: string } = {};
    const {
      id,
      isComparative: _isComparative,
      isFullScreen: _isFullScreen,
      updatePieChart: _updatePieChart,
      ...filters
    } = query;
    const headers: { [header: string]: string } = { 'x-tenant-id': `${tenantId}` };
    Object.keys(filters).forEach(
      (key) =>
        (params[key] = (query as unknown as { [key: string]: { toString: () => string } })[
          key
        ].toString())
    );
    return this.httpClient.get<T>(`${this.route}/${id}`, {
      params,
      headers,
    });
  }

  /**
   * Boundaries to bucket a column by, computed by the engine from the column's own data (WB-126).
   * Each bound is read as `column < bound`, so N buckets come back as N-1 bounds.
   */
  getColumnBuckets(
    tenantId: id,
    table: string,
    column: string,
    method: BucketMethod,
    count?: number
  ): Observable<IColumnBuckets> {
    const params: { [key: string]: string } = { table, column, method };
    if (count !== undefined) params['count'] = String(count);

    return this.httpClient.get<IColumnBuckets>(`${this.route}/column-buckets`, {
      params,
      headers: { 'x-tenant-id': `${tenantId}` },
    });
  }

  saveQueryConfig(queryConfigData: IQueryConfigData, tenantId: id): Observable<IQueryConfigData> {
    const params: { [key: string]: string } = {};
    const headers: { [header: string]: string } = { 'x-tenant-id': `${tenantId}` };

    return this.httpClient.post<IQueryConfigData>(`${this.route}/save-config`, queryConfigData, {
      params,
      headers,
    });
  }

  forecast(body: IForecastRequest, tenantId: id): Observable<IForecastResponse> {
    return this.httpClient.post<IForecastResponse>(`${this.route}/forecast`, body, {
      headers: { 'x-tenant-id': `${tenantId}` },
    });
  }

  loadSavedQueries(tenantId: id): Observable<IQueryResponseData[]> {
    const headers: { [header: string]: string } = { 'x-tenant-id': `${tenantId}` };
    return this.httpClient.get<IQueryResponseData[]>(`${this.route}/config`, {
      headers,
    });
  }

  /** A saved query as the server stores it (`{ id, name, config, ... }`), unresolved. */
  loadQuery(queryId: id, tenantId: id): Observable<IQueryResponseData> {
    return this.httpClient.get<IQueryResponseData>(`${this.route}/${queryId}`, {
      headers: { 'x-tenant-id': `${tenantId}` },
    });
  }

  loadAttributeMetadata(tenantId: id): Observable<IAttributeMetadataItem[]> {
    const headers: { [header: string]: string } = { 'x-tenant-id': `${tenantId}` };
    return this.httpClient.get<IAttributeMetadataItem[]>(`${this.route}/attribute-metadata`, { headers });
  }

  /** Full catalog: curated rows (incl. hidden) unioned with uncatalogued physical columns. */
  loadAttributeMetadataCatalog(tenantId: id): Observable<IAttributeMetadataItem[]> {
    const headers: { [header: string]: string } = { 'x-tenant-id': `${tenantId}` };
    return this.httpClient.get<IAttributeMetadataItem[]>(`${this.route}/attribute-metadata/catalog`, {
      headers,
    });
  }

  /** Per-table summary (attribute totals + configured counts) for the accordion/selector. */
  loadAttributeMetadataTables(tenantId: id): Observable<IAttributeMetadataSummary[]> {
    const headers: { [header: string]: string } = { 'x-tenant-id': `${tenantId}` };
    return this.httpClient.get<IAttributeMetadataSummary[]>(`${this.route}/attribute-metadata/tables`, {
      headers,
    });
  }

  /** Catalog rows for a single table (lazy-loaded on expand). */
  loadAttributeMetadataByTable(table: string, tenantId: id): Observable<IAttributeMetadataItem[]> {
    const headers: { [header: string]: string } = { 'x-tenant-id': `${tenantId}` };
    return this.httpClient.get<IAttributeMetadataItem[]>(`${this.route}/attribute-metadata/catalog`, {
      headers,
      params: { table },
    });
  }

  /** Search matching rows across all tables (server enforces a 3-char minimum). */
  searchAttributeMetadata(search: string, tenantId: id): Observable<IAttributeMetadataItem[]> {
    const headers: { [header: string]: string } = { 'x-tenant-id': `${tenantId}` };
    return this.httpClient.get<IAttributeMetadataItem[]>(`${this.route}/attribute-metadata/catalog`, {
      headers,
      params: { search },
    });
  }

  /** Create or update the metadata for a single table attribute. */
  saveAttributeMetadata(item: IUpsertAttributeMetadata, tenantId: id): Observable<IAttributeMetadataItem> {
    const headers: { [header: string]: string } = { 'x-tenant-id': `${tenantId}` };
    return this.httpClient.post<IAttributeMetadataItem>(`${this.route}/attribute-metadata`, item, {
      headers,
    });
  }

  /** Visible table metadata; unlike the catalog, readable by any user with basic access. */
  loadTableMetadata(tenantId: id): Observable<ITableMetadataItem[]> {
    const headers: { [header: string]: string } = { 'x-tenant-id': `${tenantId}` };
    return this.httpClient.get<ITableMetadataItem[]>(`${this.route}/table-metadata`, { headers });
  }

  /** Per-table metadata catalog: curated table rows unioned with uncatalogued physical tables. */
  loadTableMetadataCatalog(tenantId: id): Observable<ITableMetadataItem[]> {
    const headers: { [header: string]: string } = { 'x-tenant-id': `${tenantId}` };
    return this.httpClient.get<ITableMetadataItem[]>(`${this.route}/table-metadata/catalog`, {
      headers,
    });
  }

  /** Create or update the metadata for a single table. */
  saveTableMetadata(item: IUpsertTableMetadata, tenantId: id): Observable<ITableMetadataItem> {
    const headers: { [header: string]: string } = { 'x-tenant-id': `${tenantId}` };
    return this.httpClient.post<ITableMetadataItem>(`${this.route}/table-metadata`, item, {
      headers,
    });
  }

  /** Signs off a query the assistant generated, as the calling user (WB-180). */
  validateQueryConfig(queryId: id, tenantId: id): Observable<IAiValidation> {
    const headers: { [header: string]: string } = { 'x-tenant-id': `${tenantId}` };
    return this.httpClient.post<IAiValidation>(`${this.route}/config/${queryId}/validate`, {}, { headers });
  }

  deleteQueryConfig(queryId: id, tenantId: id): Observable<{ success: boolean }> {
    const headers: { [header: string]: string } = { 'x-tenant-id': `${tenantId}` };
    return this.httpClient.delete<{ success: boolean }>(`${this.route}/config/${queryId}`, {
      headers,
    });
  }

  /**
   * Generate SQL and execute it in a single, server-side step. This is the ONLY way the
   * front fetches chart data — it never generates, receives or persists raw SQL. (The
   * separate generate/execute endpoints exist server-side for admin debugging only.)
   * Returns the response shape ({ query, data }); callers unwrap `data` for the rows.
   * When the engine answers PATH_CHOICE_REQUIRED (GOP-91, a 201 with no rows) it errors with
   * `PathChoiceRequiredError` instead, so no caller reads it as an empty result.
   */
  buildAndRun(queryConfig: QueryConfig, tenantId: id): Observable<unknown> {
    const headers: { [header: string]: string } = { 'x-tenant-id': `${tenantId}` };
    const { schema, version, partialResultAllowed, ...config } = queryConfig;
    const body = {
      schema,
      version,
      partialResultAllowed: partialResultAllowed ?? null,
      config: { ...config },
    };
    return this.httpClient.post<unknown>(`${this.route}/build-run`, body, { headers }).pipe(
      map((response) => {
        if (isPathChoiceRequired(response)) throw new PathChoiceRequiredError(response);
        return response;
      })
    );
  }

  /**
   * Joins the engine would walk for each query level (main query, then each subquery), exactly as
   * it plans them from `table_relation`. Feeds the query graph of the query form (WB-155).
   * `joinRelations` (GOP-74), one entry per level, are the hops each config fixes: the answer draws
   * the path they walk and still lists the equally short paths they chose from.
   */
  loadJoinPaths(tenantId: id, levels: string[][], joinRelations?: JoinRelationEntry[][]): Observable<JoinPathLevel[]> {
    const body = joinRelations ? { levels, joinRelations } : { levels };
    return this.httpClient.post<JoinPathLevel[]>(`${this.route}/join-paths`, body, { headers: { 'x-tenant-id': `${tenantId}` } });
  }
}
